import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/TranslationContext';
import { translationAPI } from '../lib/api';
import { secureStorage } from '../lib/secureStorage';

export interface TranslatableContent {
  aiAnalysis?: string;
  painPoints?: string[];
  reasoning?: string;
  suggestedActions?: string[];
  keyInsights?: string[];
  intent?: string;
  description?: string;
}

export interface TranslatedResult extends TranslatableContent {
  isTranslating: boolean;
}

const memoryCache = new Map<string, string>();

function cacheKey(text: string, lang: string): string {
  let hash = 0;
  for (let i = 0; i < text.length; i++) {
    hash = ((hash << 5) - hash + text.charCodeAt(i)) | 0;
  }
  return `otf:v2:${Math.abs(hash).toString(36)}:${lang}`;
}

function getCached(text: string | undefined, lang: string): string | undefined {
  if (!text) return undefined;
  return memoryCache.get(cacheKey(text, lang)) ?? text;
}

function buildStableKey(content: TranslatableContent): string {
  return [
    content.aiAnalysis ?? '',
    content.painPoints?.join('|') ?? '',
    content.reasoning ?? '',
    content.suggestedActions?.join('|') ?? '',
    content.keyInsights?.join('|') ?? '',
    content.intent ?? '',
    content.description ?? '',
  ].join('::');
}

export function useTranslateOnFly(content: TranslatableContent): TranslatedResult {
  const { language } = useTranslation();
  const { currentOrganization } = useAuth();
  const [translated, setTranslated] = useState<TranslatableContent>(content);
  const [isTranslating, setIsTranslating] = useState(false);
  const stableKey = buildStableKey(content);
  const prevKeyRef = useRef(stableKey);

  useEffect(() => {
    prevKeyRef.current = stableKey;

    if (language === 'en') {
      setTranslated(content);
      setIsTranslating(false);
      return;
    }

    const buildFromCache = (): TranslatableContent => ({
      aiAnalysis: getCached(content.aiAnalysis, language),
      painPoints: content.painPoints?.map((p) => getCached(p, language) ?? p),
      reasoning: getCached(content.reasoning, language),
      suggestedActions: content.suggestedActions?.map((a) => getCached(a, language) ?? a),
      keyInsights: content.keyInsights?.map((i) => getCached(i, language) ?? i),
      intent: getCached(content.intent, language),
      description: getCached(content.description, language),
    });

    const textsNeedingTranslation: string[] = [];
    const collectIfMissing = (text: string | undefined) => {
      if (!text) return;
      const key = cacheKey(text, language);
      if (!memoryCache.has(key)) textsNeedingTranslation.push(text);
    };

    collectIfMissing(content.aiAnalysis);
    collectIfMissing(content.reasoning);
    collectIfMissing(content.intent);
    collectIfMissing(content.description);
    content.painPoints?.forEach(collectIfMissing);
    content.suggestedActions?.forEach(collectIfMissing);
    content.keyInsights?.forEach(collectIfMissing);

    if (textsNeedingTranslation.length === 0) {
      setTranslated(buildFromCache());
      setIsTranslating(false);
      return;
    }

    let cancelled = false;
    setIsTranslating(true);
    setTranslated(buildFromCache());

    (async () => {
      const stillMissing: string[] = [];
      for (const text of textsNeedingTranslation) {
        const key = cacheKey(text, language);
        const stored = await secureStorage.getItem(key);
        if (stored && stored !== text && !cancelled) {
          memoryCache.set(key, stored);
        } else if (!cancelled) {
          stillMissing.push(text);
        }
      }

      if (cancelled) return;
      setTranslated(buildFromCache());

      if (stillMissing.length === 0) {
        setIsTranslating(false);
        return;
      }

      const orgId = currentOrganization?.id;
      if (!orgId) {
        setIsTranslating(false);
        return;
      }

      try {
        const uniqueTexts = [...new Set(stillMissing)];
        const { data } = await translationAPI.translate(orgId, {
          texts: uniqueTexts,
          targetLanguage: language as 'es' | 'pt',
          context: 'CRM lead enrichment analysis',
        });

        if (cancelled) return;

        uniqueTexts.forEach((originalText, i) => {
          const translation = data.translations[i];
          if (translation && translation !== originalText) {
            const key = cacheKey(originalText, language);
            memoryCache.set(key, translation);
            secureStorage.setItem(key, translation).catch(() => {});
          }
        });

        setTranslated(buildFromCache());
      } catch {
        // On failure, keep showing whatever we have (original English or partial cache)
      } finally {
        if (!cancelled) setIsTranslating(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [language, stableKey, currentOrganization?.id]);

  return { ...translated, isTranslating };
}
